var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { z } from 'zod';
const configurationPowerManagementResponse = z.object({
    powerMode: z.string(),
    supportedPowerModes: z.array(z.string()),
    status: z.string(),
    supportedStatuses: z.array(z.string()),
    standbyTimeout: z.string(),
    supportedStandbyTimeouts: z.array(z.string()),
});
const configurationSystemDeviceIdentityResponse = z.object({
    articleNumber: z.string(),
    hardwareVersion: z.string(),
    modelName: z.string(),
    productName: z.string(),
    serialNumber: z.string(),
});
const configurationSystemStatusResponse = z.object({
    errorCode: z.string(),
    errorMessage: z.string(),
    currentUptime: z.number(),
    totalUptime: z.number(),
    firstUsed: z.string(),
    inUse: z.boolean(),
    sharing: z.boolean(),
});
const configurationSystemUpdatesCurrentResponse = z.object({
    date: z.string(),
    version: z.string(),
});
const operationsResponse = z.object({
    status: z.number(),
    message: z.string(),
});
function isResp(schema, resp) {
    return schema.safeParse(resp).success;
}
export class BarcoClickShare {
    constructor(http, state, log, framework) {
        this.http = http;
        this.state = state;
        // other class properties and constructor
        this.authCredentials = '';
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.contentSource = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return 'barco';
            }),
            set: (source, tags) => __awaiter(this, void 0, void 0, function* () {
                return 'barco';
            }),
        };
        this.contentState = {
            get: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpGetJson({ cmd: `/configuration/system/status` }, configurationSystemStatusResponse, tags);
                return resp.sharing ? 'sending' : 'none';
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.firmwareVersion.value;
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpGetJson({ cmd: '/configuration/system/power-management' }, configurationPowerManagementResponse, tags);
                return resp.status.toLowerCase() === 'on';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpPostJson({ cmd: `${power ? '/operations/wakeup' : '/operations/standby'}` }, operationsResponse, tags);
                return resp.status === 202 ? power : this.state.power.value;
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                controls: ['power', 'contentState'],
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpGetJson({ cmd: `/configuration/system/updates/current` }, configurationSystemUpdatesCurrentResponse, ['configure']);
            const identity = yield this.httpGetJson({ cmd: `/configuration/system/device-identity` }, configurationSystemDeviceIdentityResponse, ['identity', 'configure']);
            this.state.serialNumber.update(identity.serialNumber);
            this.state.firmwareVersion.update(`SW: ${resp.version} (HW: ${identity.hardwareVersion})`);
        });
    }
    onInitialize(credentials) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const username = (_a = credentials.username) !== null && _a !== void 0 ? _a : 'admin';
            const password = (_b = credentials.password) !== null && _b !== void 0 ? _b : 'admin';
            this.authCredentials = btoa(`${username}:${password}`);
            // Make an http request and check for non-JSON response 'unAuthorized'.
            const resp = yield this.httpGet({ cmd: `/configuration/system/status` }, ['initialize']);
            if (((_c = resp.data) !== null && _c !== void 0 ? _c : '').toLowerCase() === 'unauthorized')
                return 'authFailed';
            // Confirm the response matches our expected JSON object.
            try {
                const data = JSON.parse((_d = resp.data) !== null && _d !== void 0 ? _d : '{}');
                if (isResp(configurationSystemStatusResponse, data)) {
                    return 'connected';
                }
            }
            catch (error) {
                return 'verifyFailed';
            }
            return 'verifyFailed';
        });
    }
    // other class methods
    httpGet(cmd, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.headers = { Authorization: `Basic ${this.authCredentials}` };
            requestOptions.tags = tags;
            const resp = yield this.http.request('get', `:4003/v2${cmd.cmd}`, requestOptions);
            return resp;
        });
    }
    httpGetJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.headers = { Authorization: `Basic ${this.authCredentials}` };
            requestOptions.tags = tags;
            const resp = yield this.http.requestJson('get', `:4003/v2${cmd.cmd}`, requestOptions);
            if (!isResp(schema, resp)) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
    httpPostJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.headers = { Authorization: `Basic ${this.authCredentials}` };
            requestOptions.tags = tags;
            const resp = yield this.http.requestJson('post', `:4003/v2${cmd.cmd}`, requestOptions);
            if (!isResp(schema, resp)) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
}
// static properties
BarcoClickShare.driverType = 'httpDriver';
BarcoClickShare.key = '-Ng02YzgYCE4nnm8wpiV';
BarcoClickShare.httpRequestOptions = {
    scheme: 'https',
    idleTimeoutMs: 20000,
};
